import { Injectable } from '@angular/core';

const DIALOG_SELECTOR = '[role="dialog"], [aria-modal="true"]';
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus automatique dans toute modale/panneau qui apparaît dans le DOM avec
 * role="dialog" ou aria-modal="true" — sans devoir instrumenter chaque
 * composant individuellement (changelog, notifications, idées, onboarding,
 * popup saisonnière…). Restaure le focus au déclencheur à la fermeture.
 *
 * Complète (et ne remplace pas) la gestion dédiée déjà présente dans
 * RcModalComponent/RcConfirmationComponent : les deux convergent vers la
 * même cible (on ne ré-agit pas si le composant a déjà focalisé lui-même),
 * donc aucun conflit — ce service est le filet de sécurité pour tout le
 * reste, présent et futur.
 *
 * Démarré une seule fois au bootstrap (voir AppComponent.ngOnInit).
 */
@Injectable({ providedIn: 'root' })
export class DialogAutoFocusService {
  private observer?: MutationObserver;
  private readonly previouslyFocused = new WeakMap<Element, HTMLElement>();

  start(): void {
    if (this.observer) return;
    this.observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => this.handleAdded(node));
        mutation.removedNodes.forEach(node => this.handleRemoved(node));
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private handleAdded(node: Node): void {
    if (!(node instanceof HTMLElement)) return;
    this.findDialogs(node).forEach(dialog => this.focusInto(dialog));
  }

  private handleRemoved(node: Node): void {
    if (!(node instanceof HTMLElement)) return;
    this.findDialogs(node).forEach(dialog => this.restoreFocus(dialog));
  }

  private findDialogs(node: HTMLElement): HTMLElement[] {
    const self = node.matches(DIALOG_SELECTOR) ? [node] : [];
    const descendants = Array.from(node.querySelectorAll<HTMLElement>(DIALOG_SELECTOR));
    return [...self, ...descendants];
  }

  private focusInto(dialog: HTMLElement): void {
    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body) {
      this.previouslyFocused.set(dialog, active);
    }
    // Laisser Angular terminer son rendu / l'animation d'ouverture avant de
    // mesurer les éléments focusables (largeur/hauteur encore nulles sinon).
    setTimeout(() => {
      if (!document.body.contains(dialog)) return;
      // Déjà focalisé par le composant lui-même (RcModalComponent…) : ne pas
      // lui voler la main, ni redéplacer le focus une seconde fois.
      if (dialog.contains(document.activeElement)) return;
      const focusable = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable ?? dialog).focus({ preventScroll: true });
    });
  }

  private restoreFocus(dialog: HTMLElement): void {
    const target = this.previouslyFocused.get(dialog);
    this.previouslyFocused.delete(dialog);
    if (!target || !document.contains(target)) return;
    // L'utilisateur a déjà focalisé autre chose entre-temps : ne pas le lui voler.
    const active = document.activeElement;
    if (active && active !== document.body && !dialog.contains(active)) return;
    setTimeout(() => target.focus({ preventScroll: true }));
  }
}
